/**
 * Bild tauschen: ein Bild der Mail wählen, ein neues Foto hochladen und den
 * Ausschnitt so legen, dass es genau ins alte Format passt. Gespeichert wird
 * als neue Version der Mail (dazu ruft die Werkstatt `onUebernehmen`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, Loader2, Move, Replace, ZoomIn } from 'lucide-react';
import { uploadMedia } from '../../lib/supabase';
import { bildLaden } from '../BannerEditor/zuschnitt';
import {
  ZOOM_MAX, ausschnitt, begrenzen, bildErsetzen, mailBilder, zuschneiden,
  type BildZuschnitt, type MailBild,
} from '../../lib/bildTausch';
import { Modal } from '../Modal/Modal';
import '../BannerEditor/BannerEditor.css';

type Props = {
  html: string;
  onUebernehmen: (neuesHtml: string) => Promise<void>;
  onSchliessen: () => void;
};

const START: BildZuschnitt = { x: 0.5, y: 0.5, zoom: 1 };

export function BildTauschen({ html, onUebernehmen, onSchliessen }: Props) {
  const bilder = useMemo(() => mailBilder(html), [html]);
  const datei = useRef<HTMLInputElement>(null);
  const rahmen = useRef<HTMLDivElement>(null);
  const ziehen = useRef<{ px: number; py: number; z: BildZuschnitt } | null>(null);
  const [wahl, setWahl] = useState(0);
  const [neu, setNeu] = useState<{ url: string; datei: File } | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [alt, setAlt] = useState<HTMLImageElement | null>(null);
  const [z, setZ] = useState<BildZuschnitt>(START);
  const [breite, setBreite] = useState(560);
  const [zieht, setZieht] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const bild: MailBild | undefined = bilder[wahl];

  // Maße des alten Bildes: laut Tag, sonst aus dem geladenen Bild
  useEffect(() => {
    setAlt(null);
    if (!bild) return;
    let weg = false;
    bildLaden(bild.src).then((i) => { if (!weg) setAlt(i); }).catch(() => { /* Maße aus dem Tag */ });
    return () => { weg = true; };
  }, [bild?.src]); // eslint-disable-line react-hooks/exhaustive-deps

  const anzeigeBreite = bild?.breite ?? alt?.naturalWidth ?? 620;
  const anzeigeHoehe = bild?.hoehe ?? (alt ? Math.round(anzeigeBreite * alt.naturalHeight / alt.naturalWidth) : 280);
  const verhaeltnis = anzeigeBreite / anzeigeHoehe;

  useEffect(() => () => { if (neu) URL.revokeObjectURL(neu.url); }, [neu]);

  useEffect(() => {
    if (!neu) { setImg(null); return; }
    let weg = false;
    bildLaden(neu.url)
      .then((i) => { if (!weg) { setImg(i); setZ(begrenzen(i.naturalWidth, i.naturalHeight, verhaeltnis, START)); } })
      .catch(() => { if (!weg) setFehler('Das Bild ließ sich nicht laden. Bitte wähle eine JPG- oder PNG-Datei.'); });
    return () => { weg = true; };
  }, [neu, verhaeltnis]);

  useEffect(() => {
    const el = rahmen.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBreite(el.clientWidth || 560));
    ro.observe(el);
    return () => ro.disconnect();
  }, [neu]);

  const setzen = (teil: Partial<BildZuschnitt>) => {
    setZ((a) => (img ? begrenzen(img.naturalWidth, img.naturalHeight, verhaeltnis, { ...a, ...teil }) : { ...a, ...teil }));
  };

  useEffect(() => {
    const el = rahmen.current;
    if (!el || !img) return;
    const rad = (e: WheelEvent) => {
      e.preventDefault();
      setZ((a) => begrenzen(img.naturalWidth, img.naturalHeight, verhaeltnis, { ...a, zoom: a.zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08) }));
    };
    el.addEventListener('wheel', rad, { passive: false });
    return () => el.removeEventListener('wheel', rad);
  }, [img, verhaeltnis]);

  const waehlen = (f: File | undefined) => {
    if (datei.current) datei.current.value = '';
    if (!f) return;
    setFehler(null);
    if (!f.type.startsWith('image/')) { setFehler('Bitte ein Bild wählen (JPG, PNG oder WebP).'); return; }
    if (f.size > 15 * 1024 * 1024) { setFehler('Das Bild ist größer als 15 MB — bitte vorher verkleinern.'); return; }
    setNeu({ url: URL.createObjectURL(f), datei: f });
  };

  const a = img ? ausschnitt(img.naturalWidth, img.naturalHeight, verhaeltnis, z) : null;
  const massstab = a ? breite / a.sb : 1;
  const rahmenHoehe = breite / verhaeltnis;

  const verschieben = (dx: number, dy: number, basis: BildZuschnitt) => {
    if (!img || !a) return;
    const cx = basis.x * img.naturalWidth - dx / massstab;
    const cy = basis.y * img.naturalHeight - dy / massstab;
    setZ(begrenzen(img.naturalWidth, img.naturalHeight, verhaeltnis, { ...basis, x: cx / img.naturalWidth, y: cy / img.naturalHeight }));
  };

  const taste = (e: React.KeyboardEvent) => {
    const s = e.shiftKey ? 40 : 10;
    if (e.key === 'ArrowLeft') verschieben(s, 0, z);
    else if (e.key === 'ArrowRight') verschieben(-s, 0, z);
    else if (e.key === 'ArrowUp') verschieben(0, s, z);
    else if (e.key === 'ArrowDown') verschieben(0, -s, z);
    else if (e.key === '+' || e.key === '=') setzen({ zoom: z.zoom + 0.1 });
    else if (e.key === '-') setzen({ zoom: z.zoom - 0.1 });
    else return;
    e.preventDefault();
  };

  const fertig = async () => {
    if (!bild || !neu || !img) return;
    setSpeichert(true);
    setFehler(null);
    try {
      const ausschnittDatei = await zuschneiden(img, verhaeltnis, z, anzeigeBreite);
      const url = (await uploadMedia(ausschnittDatei, 'einreichungen')).url;
      await onUebernehmen(bildErsetzen(html, bild, url));
    } catch (e) {
      setFehler((e as Error).message || 'Das Bild ließ sich nicht tauschen.');
      setSpeichert(false);
    }
  };

  return (
    <Modal isOpen onClose={() => { if (!speichert) onSchliessen(); }} title="Bild tauschen" size="lg">
      {!bild ? (
        <p className="be-anleitung">In dieser Mail gibt es kein Bild, das sich tauschen lässt.</p>
      ) : (
        <>
          {bilder.length > 1 && (
            <fieldset className="be-platz" style={{ marginBottom: '0.9rem' }}>
              <legend>Welches Bild?</legend>
              {bilder.map((b, i) => (
                <label key={b.start} className={`chip${wahl === i ? ' active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input type="radio" name="tausch-bild" checked={wahl === i} disabled={speichert} onChange={() => { setWahl(i); setNeu(null); }} />
                  <img src={b.src} alt="" style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 3 }} />
                  {b.alt || `Bild ${i + 1}`}
                </label>
              ))}
            </fieldset>
          )}

          {!neu ? (
            <>
              <p className="be-anleitung">
                <Replace size={15} aria-hidden="true" /> Das aktuelle Bild ist unten zu sehen. Wähle ein neues Foto — Du legst danach
                den Ausschnitt fest, damit es genau ins Format der Mail passt ({anzeigeBreite} × {anzeigeHoehe}).
              </p>
              <div className="be-vorschau">
                <img src={bild.src} alt="Aktuelles Bild" style={{ aspectRatio: `${anzeigeBreite} / ${anzeigeHoehe}` }} />
              </div>
              <div className="be-fuss">
                <button type="button" className="btn btn-secondary" onClick={onSchliessen}>Abbrechen</button>
                <button type="button" className="btn btn-primary" onClick={() => datei.current?.click()}>
                  <ImagePlus size={16} aria-hidden="true" /> Neues Bild wählen
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="be-anleitung">
                <Move size={15} aria-hidden="true" /> Ziehe das Foto an die richtige Stelle, mit dem Regler oder dem Mausrad
                vergrößerst Du. Der Rahmen zeigt genau, was in der Mail zu sehen ist.
              </p>
              <div
                ref={rahmen}
                className={`be-rahmen${zieht ? ' be-rahmen--zieht' : ''}`}
                style={{ height: rahmenHoehe }}
                tabIndex={0}
                role="group"
                aria-label="Ausschnitt: ziehen oder mit den Pfeiltasten verschieben, mit Plus und Minus vergrößern"
                onKeyDown={taste}
                onPointerDown={(e) => {
                  if (!img) return;
                  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                  ziehen.current = { px: e.clientX, py: e.clientY, z };
                  setZieht(true);
                }}
                onPointerMove={(e) => { const s = ziehen.current; if (s) verschieben(e.clientX - s.px, e.clientY - s.py, s.z); }}
                onPointerUp={() => { ziehen.current = null; setZieht(false); }}
                onPointerCancel={() => { ziehen.current = null; setZieht(false); }}
              >
                {img && a ? (
                  <img
                    src={neu.url}
                    alt=""
                    draggable={false}
                    className="be-bild"
                    style={{ width: img.naturalWidth * massstab, height: img.naturalHeight * massstab, left: -a.sx * massstab, top: -a.sy * massstab }}
                  />
                ) : !fehler ? (
                  <div className="be-laedt"><Loader2 size={20} className="spin" aria-hidden="true" /> Bild wird geladen …</div>
                ) : null}
              </div>
              <div className="be-regler">
                <label className="be-regler-zeile">
                  <span><ZoomIn size={15} aria-hidden="true" /> Vergrößern</span>
                  <input type="range" min={1} max={ZOOM_MAX} step={0.01} value={z.zoom} onChange={(e) => setzen({ zoom: Number(e.target.value) })} disabled={!img || speichert} />
                  <span className="be-wert">{Math.round(z.zoom * 100)} %</span>
                </label>
              </div>
              <div className="be-fuss">
                <button type="button" className="btn btn-ghost" onClick={() => datei.current?.click()} disabled={speichert}>Anderes Foto</button>
                <button type="button" className="btn btn-secondary" onClick={onSchliessen} disabled={speichert}>Abbrechen</button>
                <button type="button" className="btn btn-primary" onClick={() => { void fertig(); }} disabled={!img || speichert}>
                  {speichert ? <><Loader2 size={16} className="spin" aria-hidden="true" /> Wird gespeichert …</> : <><Replace size={16} aria-hidden="true" /> Bild tauschen</>}
                </button>
              </div>
            </>
          )}
        </>
      )}
      <input ref={datei} type="file" accept="image/*" hidden onChange={(e) => waehlen(e.target.files?.[0])} />
      {fehler && <p className="field-error" role="alert">{fehler}</p>}
    </Modal>
  );
}
